import React, { useMemo, useCallback } from 'react';
import { graphql, useRefetchableFragment } from 'react-relay';
import {
	MenuListItem,
	MenuList,
	MenuSection,
	MenuSectionHeading,
} from '@atlaskit/navigation-system';
import { useIntl } from '@atlassian/jira-intl';
import { MENU_ID_PLANS } from '@atlassian/jira-navigation-apps-sidebar-nav4-context/src/common/constants.tsx';
import { useSidebarNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-context/src/controllers/sidebar-context/index.tsx';
import { useRefetchWhenIsSelected } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/controller/use-refetch-when-is-selected/index.tsx';
import { useSubscribeAndUpdateFavorites } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/services/set-favorite/index.tsx';
import { useStableItems } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/utils/use-stable-items/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useReorderableStarredItems } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/utils/use-reorderable-starred-items.tsx';
import { NestedItemDropTarget } from '@atlassian/navigation-drag-and-drop';
import { PlansEmptyContent } from '../empty-content/index.tsx';
import { usePlanReorderingIds } from '../../../../services/use-plan-reordering-ids.tsx';
import type { PlansListRefactored$key } from './__generated__/PlansListRefactored.graphql.ts';
import messages from './messages.tsx';
import QUERY, {
	type PlanListRefactoredQuery,
} from './__generated__/PlanListRefactoredQuery.graphql.ts';
import { PlanMenuItemRefactored } from './plan-menu-item/PlanMenuItemRefactored.tsx';

type Props = {
	fragmentRef: PlansListRefactored$key;
};

const MAX_RECENT_PLANS = 5;

/**
 * We intentionally request 15 starred and 20 recent plans.
 * If all 15 starred plans were recently visited, we still show 5 recent plans as well.
 */
export const PlansListRefactored = ({ fragmentRef }: Props): React.JSX.Element => {
	const { isSelectedPath } = useSidebarNav4();
	const cloudId = useCloudId();
	const { getStableItems } = useStableItems();

	const [data, refetch] = useRefetchableFragment<PlanListRefactoredQuery, PlansListRefactored$key>(
		graphql`
			fragment PlansListRefactored on Query
			@refetchable(queryName: "PlansListRefactoredQuery")
			@argumentDefinitions(currentURLPath: { type: URL }) {
				jira @required(action: LOG) {
					favouritePlans: favourites(
						cloudId: $cloudId
						filter: { type: PLAN, sort: { order: DESC } }
						first: 15
					) @required(action: LOG) {
						__id
						pageInfo {
							hasNextPage
						}
						edges {
							node @required(action: LOG) {
								... on JiraPlan {
									id @required(action: LOG)
									planId
									title
									...PlanMenuItemRefactored
								}
							}
						}
					}
					recentPlans: recentItems(
						cloudId: $cloudId
						currentURL: $currentURLPath
						types: [PLAN]
						first: 20
					) @required(action: LOG) {
						__id
						edges {
							node @required(action: LOG) {
								... on JiraPlan {
									id @required(action: LOG)
									planId
									...PlanMenuItemRefactored
								}
							}
						}
					}
				}
				plansOrder: settings_navigationCustomisation(
					entityAri: $entityAriPlans
					ownerAri: $ownerAri
				) {
					sidebar {
						nodes {
							# It is necessary to suppress the lint rule, because these ARE used by useReorderableStarredItems
							# eslint-disable-next-line @atlassian/relay/unused-fields
							menuId
							# eslint-disable-next-line @atlassian/relay/unused-fields
							visible
						}
					}
				}
			}
		`,
		fragmentRef,
	);

	useRefetchWhenIsSelected({ cloudId, isSelectedPath, QUERY, refetch, menuId: MENU_ID_PLANS });

	const refetchOnChange = useCallback(
		({ onComplete }: { onComplete: () => void }) => {
			refetch({ cloudId }, { fetchPolicy: 'store-and-network', onComplete });
		},
		[cloudId, refetch],
	);

	useSubscribeAndUpdateFavorites({
		entityTypename: 'JiraPlan',
		favouriteConnectionId: data?.jira.favouritePlans.__id || '',
		recentConnectionId: data?.jira.recentPlans.__id || '',
		refetch: refetchOnChange,
	});

	const { formatMessage } = useIntl();
	const starred = formatMessage(messages.starred);
	const recent = formatMessage(messages.recent);

	const { entityAri, ownerAri, group } = usePlanReorderingIds();

	const favouritePlans = data?.jira.favouritePlans;

	const planOrder = data?.plansOrder || null;

	type PlanEdge = NonNullable<NonNullable<NonNullable<typeof favouritePlans>['edges']>[number]>;
	type PlanNode = NonNullable<PlanEdge['node']>;

	const planNodes: PlanNode[] = useMemo(
		() =>
			favouritePlans?.edges
				?.filter((edge): edge is PlanEdge => edge != null && edge !== undefined)
				?.map((edge: PlanEdge) => edge.node)
				.filter((node): node is PlanNode => node != null || node !== undefined)

				.filter((node) => node) ?? [],
		[favouritePlans?.edges],
	);

	const favorites = useReorderableStarredItems<PlanNode>({
		favourites: planNodes,
		lastOrder: planOrder?.sidebar?.nodes,
		getId: (item) => item.id || '',
		getLabel: (item) => item?.title?.toString() || '',
		getHasNextPage: () => Boolean(favouritePlans?.pageInfo?.hasNextPage),
		renderMenuItem: (edge, currentItems, currentOrder, item) => {
			return (
				<PlanMenuItemRefactored
					key={edge.id}
					fragmentRef={edge}
					favouriteConnectionId={data?.jira.favouritePlans.__id || ''}
					currentItems={currentItems}
					currentOrder={currentOrder}
					item={item}
				/>
			);
		},
		maxItems: 15,
	});

	const recentPlans = useMemo(() => {
		if (!data) return { list: [], hasItems: false };

		const nodes =
			data.jira.recentPlans.edges
				?.filter(Boolean)
				.filter((edge) => edge.node.id && !favorites.idSet.has(edge.node.id))
				.map((edge) => edge.node)
				.slice(0, MAX_RECENT_PLANS) ?? [];

		const stableOrderedNodes = getStableItems({ nodes, idKey: 'id' });

		const list = stableOrderedNodes.map((node) => (
			<PlanMenuItemRefactored
				key={node.planId}
				fragmentRef={node}
				favouriteConnectionId={data.jira.favouritePlans.__id}
			/>
		));

		return { list, hasItems: Boolean(list.length) };
	}, [data, favorites.idSet, getStableItems]);

	if (!favorites.hasItems && !recentPlans.hasItems) {
		return <PlansEmptyContent />;
	}

	return (
		<>
			{favorites.hasItems && (
				<NestedItemDropTarget
					currentItems={favorites.currentItems}
					currentOrder={favorites.currentOrder}
					entityAri={entityAri}
					group={group}
					ownerAri={ownerAri}
				>
					<MenuListItem>
						<MenuSection>
							<MenuSectionHeading>{starred}</MenuSectionHeading>
							<MenuList>{favorites.list}</MenuList>
						</MenuSection>
					</MenuListItem>
				</NestedItemDropTarget>
			)}
			{recentPlans.hasItems && (
				<MenuListItem>
					<MenuSection>
						<MenuSectionHeading>{recent}</MenuSectionHeading>
						<MenuList>{recentPlans.list}</MenuList>
					</MenuSection>
				</MenuListItem>
			)}
		</>
	);
};
